import { useState } from "react";
import { Spin } from "antd";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ThunderboltOutlined,
  MenuOutlined,
  CloseOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useConfiguration } from "../../../../hooks/useConfiguration";
import { useCleanupZeroTotalBills } from "../../../../hooks/useCleanup";
import { App } from "antd";
import ProfileDropdown from "../../../../components/ProfileDropdown";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { logout, setAccess } from "../../../../store/authSlice";
import { denyAccess } from "../../../../api/access";
import { modalStyles } from "../../../../utils/modalStyles";
import { getApiErrorMessage } from "../../../../utils/translations";
import { useViewport } from "../../../../hooks/useViewport";
import { Modal } from "antd";
import DeleteConfirmModal from "../DeleteConfirmModal";
import type { ConfigOutletContext } from "../../Configuration";
import type { CleanupZeroTotalBillsResponse } from "../../../../api/cleanup";
import { R } from "./pageTokens";
import { CleanupSection } from "./CleanupSection";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import { RestaurantSettingsSection } from "./RestaurantSettingsSection";

/**
 * Страницата „Маркиране" в Конфигурация — настройките НА РЕСТОРАНТА (таблица
 * `configuration` в базата на ресторанта, общи за всички терминали) като
 * секции с редове (RestaurantSettingsSection) + секцията „Поддръжка" с
 * оптимизацията на базата и модалите ѝ, които са тук. Личните настройки на
 * сервитьора се сменят от самото „Маркиране". Единственият акцент е
 * кехлибареният — тежестта е в нюанса.
 */
const OrderingSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { isNarrow, onOpenMobileMenu } =
    useOutletContext<ConfigOutletContext>();
  const { isMobile, isTablet } = useViewport();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const access = useAppSelector((s) => s.auth.access);
  const restaurantAccesses = useAppSelector((s) => s.auth.restaurantAccesses);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const displayName = names || username || "";
  const canCleanup = access && restaurantAccesses;
  // Оптимизацията минава през облака И през всяка каса: трие сметки тук и
  // зачиства регистъра на боновете там, за да може номерацията да тръгне
  // отначало без разминаване. В локален режим няма нито едното — бутонът се
  // показва, но е заключен, за да е ясно защо.
  const isEdgeLocal = useIsEdgeLocal();

  // Всички настройки идват с /user/info; докато не са — спинер.
  const { isLoaded } = useConfiguration();
  const isLoading = !isLoaded;

  const { mutate: runCleanup, isPending: isCleaningUp } =
    useCleanupZeroTotalBills();

  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const [cleanupResult, setCleanupResult] =
    useState<CleanupZeroTotalBillsResponse | null>(null);

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  const handleCleanupConfirm = () =>
    new Promise<void>((resolve, reject) => {
      runCleanup(undefined, {
        onSuccess: (data) => {
          setCleanupConfirmOpen(false);
          setCleanupResult(data);
          if (data.deleted_bills_count === 0) {
            message.info(data.message || t("nonFiscalDisplay.cleanupNoBills"));
          } else {
            message.success(
              t("nonFiscalDisplay.cleanupSuccess", {
                count: data.deleted_bills_count,
              }),
            );
          }
          resolve();
        },
        onError: (err) => {
          const axiosErr = err as { response?: { data?: { detail?: string } } };
          // BE-то праща само КОД в `detail` — текстовете са тук, в преводите.
          // Всичко друго (напр. „Missing permission", вече преведено от
          // интерсептора) минава по общия път.
          const code = axiosErr?.response?.data?.detail;
          const key = `nonFiscalDisplay.cleanupErrors.${code}`;
          message.error(
            code && i18n.exists(key)
              ? t(key)
              : getApiErrorMessage(err, t("nonFiscalDisplay.cleanupError")),
          );
          reject();
        },
      });
    });

  const resultRows = cleanupResult
    ? [
        {
          label: t("nonFiscalDisplay.cleanupDeletedBills"),
          value: cleanupResult.deleted_bills_count,
        },
        {
          label: t("nonFiscalDisplay.cleanupDeletedReports"),
          value: cleanupResult.deleted_reports,
        },
        {
          label: t("nonFiscalDisplay.cleanupRecalculated"),
          value: cleanupResult.recalculated_reports,
        },
        {
          label: t("nonFiscalDisplay.cleanupRemaining"),
          value: cleanupResult.remaining_bills,
        },
        {
          label: t("nonFiscalDisplay.cleanupNewMaxBillId"),
          value: cleanupResult.new_max_bill_id,
        },
      ]
    : [];

  const resultModalWidth = isMobile ? undefined : isTablet ? 440 : 480;

  return (
    <div className="h-full flex flex-col" style={{ background: R.bg }}>
      <style>{`
        @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Top bar */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
          gap: 10,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1 }}
        >
          {isNarrow && (
            <button
              onClick={onOpenMobileMenu}
              aria-label="Open menu"
              style={{
                height: 34,
                width: 34,
                borderRadius: 8,
                background: "transparent",
                border: "none",
                color: R.ink,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
          )}
          <h1
            style={{
              fontSize: isNarrow ? 18 : 22,
              fontWeight: 700,
              letterSpacing: -0.5,
              margin: 0,
              color: R.ink,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("configSidebar.ordering")}
          </h1>
        </div>

        <div
          className="flex items-center"
          style={{ gap: isNarrow ? 6 : 10, flexShrink: 0 }}
        >
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Body: секциите с настройките на ресторанта + „Поддръжка" */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center p-12">
            <Spin size="large" />
          </div>
        ) : (
          <div className="mx-auto flex min-h-full max-w-[1100px] flex-col gap-4 bg-canvas px-3.5 pb-6 pt-4 sm:px-7 sm:pb-8 sm:pt-6">
            <RestaurantSettingsSection />
            {canCleanup && (
              <CleanupSection
                onRun={() => setCleanupConfirmOpen(true)}
                running={isCleaningUp}
                offline={isEdgeLocal}
              />
            )}
          </div>
        )}
      </div>

      {/* Confirm cleanup modal */}
      <DeleteConfirmModal
        open={cleanupConfirmOpen}
        title={t("nonFiscalDisplay.cleanupTitle")}
        description={t("nonFiscalDisplay.cleanupConfirmContent")}
        confirmLabel={t("nonFiscalDisplay.cleanupConfirmOk")}
        cancelLabel={t("nonFiscalDisplay.cleanupConfirmCancel")}
        icon={<ThunderboltOutlined style={{ fontSize: 18 }} />}
        onConfirm={handleCleanupConfirm}
        onCancel={() => setCleanupConfirmOpen(false)}
      />

      {/* Result modal */}
      <Modal
        open={cleanupResult !== null}
        onCancel={() => setCleanupResult(null)}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={resultModalWidth}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div
          style={{
            background: R.surface,
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          }}
        >
          <div
            style={{
              padding: isNarrow ? "14px 16px" : "18px 22px",
              borderBottom: `1px solid ${R.border}`,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: R.brandTint,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--color-brand-deep-ink)",
                flexShrink: 0,
              }}
            >
              <CheckCircleOutlined style={{ fontSize: 20 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: R.textMuted,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  lineHeight: 1,
                }}
              >
                {t("nonFiscalDisplay.cleanupTitle")}
              </div>
              <div
                style={{
                  fontSize: isNarrow ? 16 : 18,
                  fontWeight: 700,
                  letterSpacing: -0.3,
                  color: R.ink,
                  lineHeight: 1.2,
                  marginTop: 4,
                }}
              >
                {t("nonFiscalDisplay.cleanupResultTitle")}
              </div>
            </div>
            <button
              onClick={() => setCleanupResult(null)}
              aria-label="Close"
              style={{
                height: 30,
                width: 30,
                borderRadius: 6,
                background: "transparent",
                border: "none",
                color: R.textMuted,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 120ms, color 120ms",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-shade-1)";
                e.currentTarget.style.color = R.ink;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = R.textMuted;
              }}
            >
              <CloseOutlined style={{ fontSize: 14 }} />
            </button>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: isNarrow ? "12px 16px" : "16px 22px",
              background: R.bg,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {resultRows.map((row) => (
              <div
                key={row.label}
                style={{
                  background: R.surface,
                  border: `1px solid ${R.border}`,
                  borderRadius: 10,
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: R.textMuted,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  {row.label}
                </span>
                <span
                  style={{
                    fontFamily: R.mono,
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 14,
                    fontWeight: 700,
                    color: R.ink,
                    letterSpacing: -0.2,
                  }}
                >
                  {row.value}
                </span>
              </div>
            ))}
            {cleanupResult?.message && (
              <div
                style={{
                  background: R.surface,
                  border: `1px solid ${R.border}`,
                  borderRadius: 10,
                  padding: "10px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: R.textMuted,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  {t("nonFiscalDisplay.cleanupMessage")}
                </span>
                <span style={{ fontSize: 13, color: R.text, lineHeight: 1.4 }}>
                  {cleanupResult.message}
                </span>
              </div>
            )}
          </div>

          <div
            style={{
              borderTop: `1px solid ${R.border}`,
              padding: isNarrow ? "12px 16px" : "14px 22px",
              display: "flex",
              justifyContent: "flex-end",
              background: R.surface,
            }}
          >
            <button
              type="button"
              onClick={() => setCleanupResult(null)}
              style={{
                height: 42,
                padding: "0 22px",
                borderRadius: 10,
                border: "none",
                background: R.brand,
                color: "#fff",
                fontSize: 13.5,
                fontWeight: 700,
                letterSpacing: -0.2,
                cursor: "pointer",
                transition: "background 120ms",
                width: isMobile ? "100%" : undefined,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = R.brandDeep;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = R.brand;
              }}
            >
              {t("nonFiscalDisplay.cleanupResultClose")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderingSettingsPage;
